// Requests (CR_20260915_Request_Qty §5.2, Q16): the company-wide list, opening on the viewer's own queue — managers on
// Waiting approval, warehouse staff on To prepare, project users on In progress — and a request's page: lines,
// transfers, timeline, and the actions of each role: Approve / Reject / Re-route (manager), Prepare & dispatch /
// Close short / Not available here (the source warehouse's staff), Cancel (requester or manager). Warehouse stock
// (availability) is shown to managers only, in the approve dialog; a project user never sees a warehouse figure.
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  fitPicks,
  isLate,
  lineRemainder,
  linesText,
  pickable,
  type RequestAvailability,
  type RequestOverview,
} from '@fleetspoke/core/requests';
import { type FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { COVER_REASONS, type CoverReason, requestSource } from '@fleetspoke/core/requests';
import { canManage, useSession } from '../auth/session';
import { blockOf, DispatchBlockNotice } from '../components/DispatchBlock';
import { usePlaceNames } from '../components/NewTransferDialog';
import { Empty, Loading, LoadError, Page } from '../components/Page';
import { RequestStatusChip, useCompanyToday, useRequestPlace } from '../components/requests';
import { type RateCard, useProfileNames, useRateCards } from '../data/billing';
import { useSiteBoard } from '../data/queries';
import { useLiveInvalidate } from '../data/realtime';
import {
  requestError,
  requestKeys,
  useCancelRequest,
  useCloseRequestShort,
  useDecideRequest,
  useFulfilRequest,
  useRequest,
  useRequestAvailability,
  useRequests,
  useRequestTransfers,
  useReturnRequest,
  useIsRequestApprover,
} from '../data/requests';
import { TRANSFER_STATUS_COLOR } from '../data/workflow';
import { calendarDate, dateTime } from '../lib/time';
import { canActAt } from '../lib/zones';

export const REQUEST_FILTERS = ['waiting', 'to_prepare', 'in_progress', 'closed'] as const;
export type RequestFilter = (typeof REQUEST_FILTERS)[number];
type Me = ReturnType<typeof useSession>['me'];

/** The filter chips a role gets, and the one the list opens on (Q16). */
/** The From warehouse cell: the approved source, a wish marked "preferred", or who decides (requestSource). */
function SourceText({
  r,
  name,
}: {
  r: RequestOverview;
  name: (id: string, side: 'from') => string;
}) {
  const { t } = useTranslation();
  const src = requestSource(r);
  if (src.kind === 'chosen') return <>{name(src.siteId, 'from')}</>;
  if (src.kind === 'preferred')
    return <>{`${name(src.siteId, 'from')} (${t('requests.preferred')})`}</>;
  if (src.kind === 'pending')
    return (
      <Box component="span" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        {t('requests.source_pending')}
      </Box>
    );
  return <>—</>;
}

export function requestFilters(me: Me): { chips: RequestFilter[]; home: RequestFilter } {
  if (canManage(me)) return { chips: [...REQUEST_FILTERS], home: 'waiting' };
  if (me?.role === 'warehouse_staff')
    return { chips: ['to_prepare', 'in_progress', 'closed'], home: 'to_prepare' };
  return { chips: ['in_progress', 'closed'], home: 'in_progress' };
}

export function inFilter(r: RequestOverview, f: RequestFilter, me: Me): boolean {
  switch (f) {
    case 'waiting':
      return r.status === 'submitted';
    case 'to_prepare':
      return r.status === 'approved' && r.qty_remaining > 0 && canActAt(me, r.source_site_id);
    case 'in_progress':
      return r.status === 'submitted' || r.status === 'approved';
    case 'closed':
      return r.status !== 'submitted' && r.status !== 'approved';
  }
}

/** Money is managers' (P7): the rate card rate — project + category, else category, else the account default. */
export function dayRate(
  rates: readonly RateCard[],
  projectId: string,
  categoryId: string,
): RateCard | undefined {
  return [...rates]
    .filter(
      (r) =>
        (r.project_id === projectId || r.project_id === null) &&
        (r.category_id === categoryId || r.category_id === null),
    )
    .sort(
      (a, b) =>
        Number(b.project_id !== null) - Number(a.project_id !== null) ||
        Number(b.category_id !== null) - Number(a.category_id !== null),
    )[0];
}

export function RequestsPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { me } = useSession();
  const [params, setParams] = useSearchParams();
  const today = useCompanyToday();
  const requests = useRequests();
  const names = usePlaceNames();
  const placeOf = useRequestPlace();
  const manager = canManage(me);
  useLiveInvalidate('requests', ['asset_requests', 'transfers'], [requestKeys.all]);
  const { chips, home } = requestFilters(me);
  const wanted = params.get('filter') as RequestFilter | null;
  const filter: RequestFilter = wanted && REQUEST_FILTERS.includes(wanted) ? wanted : home;
  const warehouse = params.get('warehouse');
  const project = params.get('project');
  const rows = (requests.data ?? [])
    .filter((r) => inFilter(r, filter, me))
    .filter((r) => !warehouse || r.source_site_id === warehouse)
    .filter((r) => !project || r.project_id === project)
    .sort((a, b) =>
      filter === 'closed'
        ? (b.closed_at ?? b.updated_at).localeCompare(a.closed_at ?? a.updated_at)
        : Number(isLate(b, today)) - Number(isLate(a, today)) ||
          Number(b.priority === 'urgent') - Number(a.priority === 'urgent') ||
          a.needed_by.localeCompare(b.needed_by),
    );
  const setFilter = (f: RequestFilter) => {
    const next = new URLSearchParams(params);
    next.set('filter', f);
    setParams(next, { replace: true });
  };
  const clearScope = (key: 'warehouse' | 'project') => {
    const next = new URLSearchParams(params);
    next.delete(key);
    setParams(next, { replace: true });
  };

  return (
    <Page
      title={t('requests.title')}
      actions={
        // D3: the company switch moved to Settings → Approvals, which is where the per-project rules live too.
        // A link stays here so somebody who knew where it was is not left hunting.
        manager ? (
          <Button component={RouterLink} to="/settings/approvals" size="small" variant="outlined">
            {t('settings.approvals.title')}
          </Button>
        ) : undefined
      }
    >
      {manager ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('requests.approval_moved')}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {chips.map((f) => (
          <Chip
            key={f}
            label={`${t(`requests.filter_${f}`)} (${
              (requests.data ?? []).filter((r) => inFilter(r, f, me)).length
            })`}
            color={f === filter ? 'primary' : 'default'}
            variant={f === filter ? 'filled' : 'outlined'}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
          />
        ))}
        {warehouse ? (
          <Chip label={names(warehouse)} onDelete={() => clearScope('warehouse')} />
        ) : null}
        {project ? (
          <Chip
            label={
              requests.data?.find((r) => r.project_id === project)?.project_name ??
              t('nav.projects')
            }
            onDelete={() => clearScope('project')}
          />
        ) : null}
      </Stack>
      {requests.isLoading ? <Loading /> : null}
      {requests.error ? <LoadError error={requests.error} /> : null}
      {requests.data && !rows.length ? <Empty>{t('requests.none')}</Empty> : null}
      {rows.length ? (
        <TableContainer>
          <Table size="small" data-testid="requests-table">
            <TableHead>
              <TableRow>
                <TableCell>{t('requests.col_ref')}</TableCell>
                <TableCell>{t('requests.col_from')}</TableCell>
                <TableCell>{t('requests.col_to')}</TableCell>
                <TableCell>{t('requests.col_lines')}</TableCell>
                <TableCell>{t('requests.col_needed_by')}</TableCell>
                <TableCell>{t('requests.col_status')}</TableCell>
                <TableCell align="right">{t('requests.col_progress')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const late = isLate(r, today);
                return (
                  <TableRow
                    key={r.request_id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => void navigate(`/requests/${r.request_id}`)}
                    data-testid={`request-${r.ref}`}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {r.ref}
                      {r.priority === 'urgent' ? (
                        <Chip
                          size="small"
                          color="error"
                          sx={{ ml: 1 }}
                          label={t('requests.priority_urgent')}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <SourceText r={r} name={names} />
                    </TableCell>
                    <TableCell>
                      {placeOf(r).project} · {placeOf(r).site}
                    </TableCell>
                    <TableCell>{linesText(r)}</TableCell>
                    <TableCell
                      sx={{ whiteSpace: 'nowrap', color: late ? 'error.main' : undefined }}
                    >
                      {calendarDate(r.needed_by, i18n.language)}
                      {late ? ` · ${t('requests.late')}` : ''}
                    </TableCell>
                    <TableCell>
                      <RequestStatusChip r={r} />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {r.status === 'approved' || r.status === 'closed'
                        ? `${r.qty_dispatched} / ${r.qty_approved}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </Page>
  );
}

// ------------------------------------------------------------------------------------------------ dialogs
/** A reason that must be given: reject, close short, not available here; optional for a cancel. */
function ReasonDialog({
  title,
  help,
  confirm,
  required = true,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  help?: string;
  confirm: string;
  required?: boolean;
  busy: boolean;
  error: unknown;
  onConfirm(reason: string): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {help ? <Typography variant="body2">{help}</Typography> : null}
          <TextField
            label={
              required ? t('requests.reason') : `${t('requests.reason')} (${t('common.optional')})`
            }
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            autoFocus
          />
          {error ? <Alert severity="error">{requestError(error, t)}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          color="error"
          disabled={busy || (required && !reason.trim())}
          onClick={() => onConfirm(reason)}
        >
          {confirm}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Approve or re-route (Q6): the source warehouse, each line's quantity (0 = not approved), availability per warehouse
 * (free = available − committed to other approved requests), and the rate card's day rate × quantity.
 */
export function ApproveDialog({ r, onClose }: { r: RequestOverview; onClose(): void }) {
  const { t } = useTranslation();
  const { me } = useSession();
  const decide = useDecideRequest();
  // D2/D6: not one of this project's named approvers? You may still approve, but as recorded cover with a reason.
  const { isApprover } = useIsRequestApprover(r.project_id, me?.user_id);
  const [cover, setCover] = useState<CoverReason>('unreachable');
  const availability = useRequestAvailability(
    r.lines.map((l) => l.category_id),
    true,
  );
  const rates = useRateCards();
  const rows = availability.data ?? [];
  const warehouses = [...new Map(rows.map((a) => [a.site_id, a.site_name]))].map(([id, name]) => ({
    id,
    name,
  }));
  const free = (site: string, category: string) => {
    const a = rows.find((x) => x.site_id === site && x.category_id === category);
    return a ? Math.max(a.available - a.committed, 0) : 0;
  };
  const best = [...warehouses].sort(
    (a, b) =>
      r.lines.reduce((n, l) => n + free(b.id, l.category_id), 0) -
      r.lines.reduce((n, l) => n + free(a.id, l.category_id), 0),
  )[0]?.id;
  const [chosen, setChosen] = useState<string | null>(null);
  const source =
    chosen ??
    (r.preferred_source_site_id && warehouses.some((w) => w.id === r.preferred_source_site_id)
      ? r.preferred_source_site_id
      : (best ?? ''));
  const [qty, setQty] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      r.lines.map((l) => [l.category_id, String(l.qty_approved ?? l.qty_requested)]),
    ),
  );
  const [note, setNote] = useState('');
  const valid = r.lines.every((l) => /^\d{1,3}$/.test(qty[l.category_id] ?? ''));
  const total = r.lines.reduce((n, l) => n + Number(qty[l.category_id] ?? 0), 0);
  const money = r.lines.map((l) => {
    const rate = dayRate(rates.data ?? [], r.project_id, l.category_id);
    return { rate, cost: rate ? Number(rate.day_rate) * Number(qty[l.category_id] ?? 0) : null };
  });
  const currency = money.find((m) => m.rate)?.rate?.currency ?? '';
  const totalCost = money.reduce((n, m) => n + (m.cost ?? 0), 0);
  const commit = (e: FormEvent) => {
    e.preventDefault();
    decide.mutate(
      {
        requestId: r.request_id,
        approve: true,
        sourceId: source,
        lines: r.lines.map((l) => ({ categoryId: l.category_id, qty: Number(qty[l.category_id]) })),
        note,
        coverReason: isApprover ? null : cover,
      },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={commit}>
        <DialogTitle>
          {t(r.status === 'approved' ? 'requests.reroute_title' : 'requests.approve_title', {
            ref: r.ref,
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {availability.isLoading ? <Loading /> : null}
            {availability.error ? <LoadError error={availability.error} /> : null}
            <TextField
              select
              label={t('requests.col_warehouse')}
              value={warehouses.some((w) => w.id === source) ? source : ''}
              onChange={(e) => setChosen(e.target.value)}
              data-testid="approve-warehouse"
              sx={{ maxWidth: 360 }}
            >
              {warehouses.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.name}
                  {w.id === r.preferred_source_site_id ? ` (${t('requests.preferred')})` : ''}
                </MenuItem>
              ))}
            </TextField>
            <TableContainer>
              <Table size="small" data-testid="approve-lines">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('requests.col_category')}</TableCell>
                    <TableCell align="right">{t('requests.col_requested')}</TableCell>
                    <TableCell>{t('requests.col_approved')}</TableCell>
                    <TableCell>{t('requests.free_per_warehouse')}</TableCell>
                    <TableCell align="right">{t('requests.day_rate')}</TableCell>
                    <TableCell align="right">{t('requests.cost_per_day')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {r.lines.map((l, i) => {
                    const q = qty[l.category_id] ?? '';
                    const short = Number(q) > free(source, l.category_id);
                    return (
                      <TableRow key={l.category_id} data-testid={`approve-line-${l.category_name}`}>
                        <TableCell>
                          {l.category_name}
                          {l.spec ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {l.spec}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{l.qty_requested}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={q}
                            onChange={(e) =>
                              setQty((x) => ({ ...x, [l.category_id]: e.target.value }))
                            }
                            error={!/^\d{1,3}$/.test(q)}
                            slotProps={{
                              htmlInput: { min: 0, max: 999, 'aria-label': `${l.category_name}` },
                            }}
                            helperText={short ? t('requests.more_than_free') : undefined}
                            sx={{ width: 110 }}
                          />
                        </TableCell>
                        <TableCell data-testid="free">
                          {warehouses
                            .map((w) => {
                              const a = rows.find(
                                (x: RequestAvailability) =>
                                  x.site_id === w.id && x.category_id === l.category_id,
                              );
                              return `${w.name} ${free(w.id, l.category_id)}${
                                a?.committed
                                  ? ` (${t('requests.committed', { count: a.committed })})`
                                  : ''
                              }`;
                            })
                            .join(' · ')}
                        </TableCell>
                        <TableCell align="right">
                          {money[i]!.rate
                            ? `${money[i]!.rate!.currency} ${Number(money[i]!.rate!.day_rate).toFixed(2)}`
                            : t('requests.no_rate')}
                        </TableCell>
                        <TableCell align="right">
                          {money[i]!.cost !== null ? money[i]!.cost!.toFixed(2) : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography data-testid="approve-total">
              {t('requests.total_per_day', {
                amount: `${currency} ${totalCost.toFixed(2)}`.trim(),
              })}
            </Typography>
            {/* D6: the reason is picked, not typed — a reason written one-handed in a yard becomes "ok" */}
            {isApprover ? null : (
              <Alert severity="warning" data-testid="cover-block">
                <AlertTitle>{t('requests.cover_title')}</AlertTitle>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  {t('requests.cover_body', { project: r.project_name ?? '' })}
                </Typography>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t('requests.cover_reason')}
                  value={cover}
                  onChange={(e) => setCover(e.target.value as CoverReason)}
                  data-testid="cover-reason"
                >
                  {COVER_REASONS.map((c) => (
                    <MenuItem key={c} value={c}>
                      {t(`requests.cover_${c}`)}
                    </MenuItem>
                  ))}
                </TextField>
              </Alert>
            )}
            <TextField
              label={
                !isApprover && cover === 'other'
                  ? t('requests.note')
                  : `${t('requests.note')} (${t('common.optional')})`
              }
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required={!isApprover && cover === 'other'}
              helperText={
                !isApprover && cover === 'other' ? t('requests.cover_other_help') : undefined
              }
            />
            {decide.error ? <Alert severity="error">{requestError(decide.error, t)}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={
              !source ||
              !valid ||
              total === 0 ||
              decide.isPending ||
              (!isApprover && cover === 'other' && !note.trim())
            }
            data-testid="approve-confirm"
          >
            {isApprover
              ? t('requests.approve_confirm', { count: total })
              : t('requests.cover_approve')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/**
 * Prepare & dispatch (Q7): per line, the free assets of its category at the warehouse to tick — or a QR code typed or
 * scanned — never more than the line's remainder; one dispatched transfer per Dispatch.
 */
export function PrepareDialog({ r, onClose }: { r: RequestOverview; onClose(): void }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const phone = !useMediaQuery(theme.breakpoints.up('sm'));
  const navigate = useNavigate();
  const { me } = useSession();
  const source = r.source_site_id ?? '';
  const board = useSiteBoard(source);
  const place = useRequestPlace()(r);
  const fulfil = useFulfilRequest();
  const categories = useMemo(() => new Set(r.lines.map((l) => l.category_id)), [r.lines]);
  const candidates = (board.data ?? []).filter((a) => pickable(a, source, categories));
  const [picked, setPicked] = useState<string[]>([]);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [vehicle, setVehicle] = useState('');
  const [note, setNote] = useState('');
  const byId = new Map(candidates.map((a) => [a.asset_id, a]));
  const fit = fitPicks(
    r.lines,
    picked.map((id) => ({ asset_id: id, category_id: byId.get(id)?.category_id ?? null })),
    !!r.short_at,
  );
  const toggle = (id: string, on: boolean) =>
    setPicked((p) => (on ? [...p, id] : p.filter((x) => x !== id)));
  const addCode = () => {
    const c = code.trim().toUpperCase();
    if (!c) return;
    const a = (board.data ?? []).find((x) => x.asset_code.toUpperCase() === c);
    const line = a?.category_id ? fit.perLine.get(a.category_id) : undefined;
    // CR_20260921 L15: an out-of-service asset scanned here is named as such, not as "not here"
    if (a?.service_state === 'out_of_service') {
      setMessage(t('dispatch.blocked_out_of_service', { code: c }));
    } else if (!a || !pickable(a, source, new Set([...categories]))) {
      setMessage(
        a?.category_id && !categories.has(a.category_id)
          ? t('requests.scan_not_line', { code: c })
          : t('requests.scan_not_here', { code: c }),
      );
    } else if (picked.includes(a.asset_id)) setMessage(t('requests.scan_already', { code: c }));
    else if (!line || line.left <= 0)
      setMessage(t('requests.scan_full', { category: a.category_name ?? '—' }));
    else {
      toggle(a.asset_id, true);
      setMessage(null);
    }
    setCode('');
  };
  // a refused dispatch can be sent again by a manager with a reason (CR_20260921 L6a)
  const dispatch = (overrideReason: string | null = null) =>
    fulfil.mutate(
      { requestId: r.request_id, assetIds: fit.fits, vehicleRef: vehicle, note, overrideReason },
      { onSuccess: (res) => void navigate(`/transfers/${res.transfer_id}`) },
    );
  const block = blockOf(fulfil.error);
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md" fullScreen={phone}>
      <DialogTitle>{t('requests.prepare_title', { ref: r.ref, place: place.site })}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              label={t('requests.scan')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCode();
                }
              }}
              data-testid="pick-code"
              sx={{ flex: 1, maxWidth: 360 }}
            />
            <Button onClick={addCode}>{t('requests.scan_add')}</Button>
          </Stack>
          {message ? (
            <Alert severity="warning" onClose={() => setMessage(null)} data-testid="pick-message">
              {message}
            </Alert>
          ) : null}
          {board.isLoading ? <Loading /> : null}
          {r.lines.map((l) => {
            const slot = fit.perLine.get(l.category_id);
            // what is still to send on this line once the ticked ones go
            const left = slot?.left ?? lineRemainder(l, !!r.short_at);
            const mine = candidates.filter((a) => a.category_id === l.category_id);
            return (
              <Paper
                key={l.category_id}
                variant="outlined"
                sx={{ p: 1.5 }}
                data-testid={`pick-line-${l.category_name}`}
              >
                <Typography variant="subtitle2" data-testid="pick-counter">
                  {t('requests.pick_head', {
                    category: `${l.category_name ?? '—'}${l.spec ? ` ${l.spec}` : ''}`,
                    approved: l.qty_approved ?? 0,
                    sent: l.qty_dispatched,
                    picked: slot?.picked ?? 0,
                    left,
                  })}
                </Typography>
                {!mine.length && !board.isLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('requests.none_free')}
                  </Typography>
                ) : null}
                <Stack>
                  {mine.map((a) => {
                    const on = picked.includes(a.asset_id);
                    return (
                      <FormControlLabel
                        key={a.asset_id}
                        control={
                          <Checkbox
                            checked={on}
                            disabled={!on && (slot?.left ?? 0) <= 0}
                            onChange={(e) => toggle(a.asset_id, e.target.checked)}
                          />
                        }
                        label={`${a.asset_code}${a.zone_name ? ` · ${a.zone_name}` : ''}${
                          a.last_seen_at ? ` · ${dateTime(a.last_seen_at, i18n.language, tz)}` : ''
                        }`}
                      />
                    );
                  })}
                </Stack>
              </Paper>
            );
          })}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={`${t('requests.vehicle')} (${t('common.optional')})`}
              value={vehicle}
              onChange={(e) => setVehicle(e.target.value)}
            />
            <TextField
              label={`${t('requests.note')} (${t('common.optional')})`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
          {block ? (
            <DispatchBlockNotice
              block={block}
              manager={canManage(me)}
              pending={fulfil.isPending}
              onOverride={(reason) => dispatch(reason)}
            />
          ) : fulfil.error ? (
            <Alert severity="error">{requestError(fulfil.error, t)}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!fit.fits.length || fulfil.isPending}
          onClick={() => dispatch()}
          data-testid="pick-dispatch"
        >
          {t('requests.dispatch', { count: fit.fits.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------------------------------------------------------------------------------------------ one request
interface TimelineEvent {
  at: string;
  text: string;
}

export function RequestDetailPage() {
  const { t, i18n } = useTranslation();
  const { requestId = '' } = useParams();
  const { me } = useSession();
  const today = useCompanyToday();
  const request = useRequest(requestId);
  const transfers = useRequestTransfers(requestId);
  const places = usePlaceNames();
  const placeOf = useRequestPlace();
  const r = request.data;
  // D2: rejecting is deciding too, so a non-approver rejects as cover (the written reason is already required)
  const { isApprover } = useIsRequestApprover(r?.project_id ?? '', me?.user_id);
  const people = useProfileNames(
    [
      r?.requested_by,
      r?.decided_by,
      r?.returned_by,
      r?.short_by,
      ...(transfers.data ?? []).flatMap((x) => [x.created_by, x.received_by]),
    ].filter((x): x is string => !!x),
  );
  const cancel = useCancelRequest();
  const decide = useDecideRequest();
  const short = useCloseRequestShort();
  const back = useReturnRequest();
  const [open, setOpen] = useState<
    null | 'approve' | 'reject' | 'prepare' | 'short' | 'return' | 'cancel'
  >(null);
  useLiveInvalidate(
    `request-${requestId}`,
    ['asset_requests', 'transfers', 'transfer_lines'],
    [requestKeys.all],
  );

  if (request.isLoading) return <Loading />;
  if (request.error || !r)
    return (
      <Page title={t('requests.title')}>
        <LoadError error={request.error ?? new Error(t('requests.not_found'))} />
      </Page>
    );
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const name = (id: string | null | undefined) => (id && people.data?.get(id)) || '—';
  const at = (iso: string | null) => dateTime(iso, i18n.language, tz) ?? '—';
  const manager = canManage(me);
  const atSource = !!r.source_site_id && canActAt(me, r.source_site_id);
  const nothingSent = r.qty_dispatched === 0;
  const open_ = r.status === 'submitted' || r.status === 'approved';
  const can = {
    decide:
      manager &&
      (r.status === 'submitted' || (r.status === 'approved' && nothingSent && !r.short_at)),
    prepare: atSource && r.status === 'approved' && !r.short_at && r.qty_remaining > 0,
    return: atSource && !manager && r.status === 'approved' && nothingSent && !r.short_at,
    cancel: (r.requested_by === me?.user_id || manager) && open_ && nothingSent && !r.short_at,
  };
  const late = isLate(r, today);
  const preparers = [...new Set((transfers.data ?? []).map((x) => x.created_by).filter(Boolean))];

  const events: TimelineEvent[] = [
    { at: r.created_at, text: t('requests.ev_created', { name: name(r.requested_by) }) },
    ...(r.returned_at
      ? [
          {
            at: r.returned_at,
            text: t('requests.ev_returned', {
              name: name(r.returned_by),
              reason: r.returned_note ?? '',
            }),
          },
        ]
      : []),
    ...(r.decided_at
      ? [
          {
            at: r.decided_at,
            text:
              r.status === 'rejected'
                ? t('requests.ev_rejected', {
                    name: name(r.decided_by),
                    reason: r.decision_note ?? '',
                  })
                : r.decided_by
                  ? r.decided_as_cover
                    ? t('requests.cover_line', {
                        name: name(r.decided_by),
                        reason: t(`requests.cover_${r.cover_reason ?? 'other'}`),
                      })
                    : t('requests.ev_approved', {
                        name: name(r.decided_by),
                        warehouse: places(r.source_site_id),
                      })
                  : t('requests.ev_straight', { warehouse: places(r.source_site_id) }),
          },
        ]
      : []),
    ...(transfers.data ?? []).flatMap((x) => [
      ...(x.dispatched_at
        ? [
            {
              at: x.dispatched_at,
              text: t('requests.ev_dispatched', {
                ref: x.ref,
                count: x.transfer_lines.length,
                name: name(x.created_by),
              }),
            },
          ]
        : []),
      ...(x.received_at
        ? [{ at: x.received_at, text: t('requests.ev_received', { ref: x.ref }) }]
        : []),
    ]),
    ...(r.short_at
      ? [
          {
            at: r.short_at,
            text: t('requests.ev_short', { name: name(r.short_by), reason: r.close_note ?? '' }),
          },
        ]
      : []),
    ...(r.closed_at && r.status !== 'rejected'
      ? [
          {
            at: r.closed_at,
            text:
              r.status === 'closed'
                ? t(`requests.closed_${r.closed_reason ?? 'delivered'}`)
                : t(`requests.status_${r.status}`),
          },
        ]
      : []),
  ].sort((a, b) => a.at.localeCompare(b.at));

  return (
    <Page
      title={`${r.ref} · ${placeOf(r).project}`}
      back={
        <Link component={RouterLink} to="/requests" variant="body2">
          ← {t('requests.title')}
        </Link>
      }
    >
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <RequestStatusChip r={r} />
        {r.priority === 'urgent' ? (
          <Chip size="small" color="error" label={t('requests.priority_urgent')} />
        ) : null}
        {late ? (
          <Chip size="small" color="error" variant="outlined" label={t('requests.late')} />
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          label={`${t('requests.col_needed_by')} ${calendarDate(r.needed_by, i18n.language) ?? '—'}`}
        />
      </Stack>

      <Stack spacing={0.5} sx={{ mb: 2 }} data-testid="request-who">
        <Typography>
          {t('requests.who_to', { place: placeOf(r).site })}
          {r.requested_for ? ` · ${t('requests.who_for', { who: r.requested_for })}` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('requests.who_requested', { name: name(r.requested_by), when: at(r.created_at) })}
        </Typography>
        {r.status === 'approved' || r.status === 'closed' ? (
          <Typography variant="body2" color="text.secondary">
            {r.decided_by
              ? t('requests.who_approved', {
                  name: name(r.decided_by),
                  warehouse: places(r.source_site_id),
                })
              : t('requests.ev_straight', { warehouse: places(r.source_site_id) })}
          </Typography>
        ) : r.preferred_source_site_id ? (
          <Typography variant="body2" color="text.secondary">
            {t('requests.who_preferred', { warehouse: places(r.preferred_source_site_id) })}
          </Typography>
        ) : null}
        {preparers.length ? (
          <Typography variant="body2" color="text.secondary">
            {t('requests.who_prepared', { names: preparers.map(name).join(', ') })}
          </Typography>
        ) : null}
        {r.note ? <Typography variant="body2">{r.note}</Typography> : null}
      </Stack>
      {r.returned_note && r.status === 'submitted' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('requests.returned_alert', { reason: r.returned_note })}
        </Alert>
      ) : null}
      {r.status === 'rejected' && r.decision_note ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('requests.rejected_alert', { reason: r.decision_note })}
        </Alert>
      ) : null}
      {r.short_at ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('requests.short_alert', { reason: r.close_note ?? '' })}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {can.prepare ? (
          <Button
            variant="contained"
            onClick={() => setOpen('prepare')}
            data-testid="action-prepare"
          >
            {t('requests.prepare')}
          </Button>
        ) : null}
        {can.decide ? (
          <Button
            variant={can.prepare ? 'outlined' : 'contained'}
            onClick={() => setOpen('approve')}
            data-testid="action-approve"
          >
            {r.status === 'approved' ? t('requests.reroute') : t('requests.approve')}
          </Button>
        ) : null}
        {can.decide ? (
          <Button color="error" onClick={() => setOpen('reject')} data-testid="action-reject">
            {t('requests.reject')}
          </Button>
        ) : null}
        {can.prepare ? (
          <Button color="warning" onClick={() => setOpen('short')} data-testid="action-short">
            {t('requests.close_short')}
          </Button>
        ) : null}
        {can.return ? (
          <Button color="warning" onClick={() => setOpen('return')} data-testid="action-return">
            {t('requests.return')}
          </Button>
        ) : null}
        {can.cancel ? (
          <Button color="inherit" onClick={() => setOpen('cancel')} data-testid="action-cancel">
            {t('requests.cancel')}
          </Button>
        ) : null}
      </Stack>

      <Typography variant="subtitle1">{t('requests.lines_title')}</Typography>
      <TableContainer sx={{ mb: 3 }}>
        <Table size="small" data-testid="request-lines">
          <TableHead>
            <TableRow>
              <TableCell>{t('requests.col_category')}</TableCell>
              <TableCell>{t('requests.col_spec')}</TableCell>
              <TableCell align="right">{t('requests.col_requested')}</TableCell>
              <TableCell align="right">{t('requests.col_approved')}</TableCell>
              <TableCell align="right">{t('requests.col_dispatched')}</TableCell>
              <TableCell align="right">{t('requests.col_received')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {r.lines.map((l) => (
              <TableRow key={l.category_id}>
                <TableCell>{l.category_name}</TableCell>
                <TableCell>{l.spec ?? ''}</TableCell>
                <TableCell align="right">{l.qty_requested}</TableCell>
                <TableCell align="right">{l.qty_approved ?? '—'}</TableCell>
                <TableCell align="right">{l.qty_dispatched}</TableCell>
                <TableCell align="right">{l.qty_received}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1">{t('requests.transfers_title')}</Typography>
      {transfers.data?.length ? (
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small" data-testid="request-transfers">
            <TableHead>
              <TableRow>
                <TableCell>{t('transfers.ref')}</TableCell>
                <TableCell>{t('transfers.assets')}</TableCell>
                <TableCell>{t('requests.vehicle')}</TableCell>
                <TableCell>{t('transfers.status')}</TableCell>
                <TableCell>{t('movements.dispatched')}</TableCell>
                <TableCell>{t('movements.received')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.data.map((x) => (
                <TableRow key={x.id}>
                  <TableCell>
                    <Link component={RouterLink} to={`/transfers/${x.id}`}>
                      {x.ref}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {x.transfer_lines.map((l) => l.assets?.asset_code ?? '—').join(', ')}
                  </TableCell>
                  <TableCell>{x.vehicle_ref ?? ''}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={TRANSFER_STATUS_COLOR[x.status]}
                      label={t(`m.transfer.status.${x.status}`)}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{at(x.dispatched_at)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{at(x.received_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('requests.no_transfers')}
        </Typography>
      )}

      <Typography variant="subtitle1">{t('requests.timeline')}</Typography>
      <Box component="ul" sx={{ pl: 2, mt: 0.5 }} data-testid="request-timeline">
        {events.map((e, i) => (
          <li key={i}>
            <Typography variant="body2">
              <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>
                {at(e.at)}
              </Box>
              {e.text}
            </Typography>
          </li>
        ))}
      </Box>

      {open === 'approve' ? <ApproveDialog r={r} onClose={() => setOpen(null)} /> : null}
      {open === 'prepare' ? <PrepareDialog r={r} onClose={() => setOpen(null)} /> : null}
      {open === 'reject' ? (
        <ReasonDialog
          title={t('requests.reject_title', { ref: r.ref })}
          confirm={t('requests.reject_confirm')}
          busy={decide.isPending}
          error={decide.error}
          onConfirm={(reason) =>
            decide.mutate(
              {
                requestId: r.request_id,
                approve: false,
                note: reason,
                // a rejection already demands a written reason, so 'other' + that note satisfies D6
                coverReason: isApprover ? null : 'other',
              },
              { onSuccess: () => setOpen(null) },
            )
          }
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'short' ? (
        <ReasonDialog
          title={t('requests.short_title', { ref: r.ref })}
          help={t('requests.short_help', { count: r.qty_remaining })}
          confirm={t('requests.short_confirm')}
          busy={short.isPending}
          error={short.error}
          onConfirm={(reason) =>
            short.mutate(
              { requestId: r.request_id, note: reason },
              { onSuccess: () => setOpen(null) },
            )
          }
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'return' ? (
        <ReasonDialog
          title={t('requests.return_title', { ref: r.ref })}
          help={t('requests.return_help')}
          confirm={t('requests.return_confirm')}
          busy={back.isPending}
          error={back.error}
          onConfirm={(reason) =>
            back.mutate(
              { requestId: r.request_id, note: reason },
              { onSuccess: () => setOpen(null) },
            )
          }
          onClose={() => setOpen(null)}
        />
      ) : null}
      {open === 'cancel' ? (
        <ReasonDialog
          title={t('requests.cancel_title', { ref: r.ref })}
          confirm={t('requests.cancel_confirm')}
          required={false}
          busy={cancel.isPending}
          error={cancel.error}
          onConfirm={(reason) =>
            cancel.mutate(
              { requestId: r.request_id, note: reason },
              { onSuccess: () => setOpen(null) },
            )
          }
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Page>
  );
}
